import {
  AssetCategoryKey,
  AssetContentType,
  AssetSetType,
  AssetType,
  OffsetPaged,
  OffsetPaginationControls,
  Optional,
  PaginationControls,
  StorageSummary,
} from '@droidmap/shared-service-contract'
import type { CaptureTimestampSource } from '@droidmap/shared-utils'

export type { CaptureTimestampSource }

/**
 * The upload stats for an asset set.
 */
export type UploadStats = {
  avgMbps: number
  maxMbps: number
  startTime: Date
  endTime: Date
  uploadDuration: number // Total elapsed time (includes pauses) in milliseconds
  activeUploadTime?: number // Time actively uploading (excludes pauses) in milliseconds
}

/* -------------------------------- AssetSet -------------------------------- */

export type AssetSet = {
  /**
   * The unique id of the asset set. (ulid)
   *
   */
  id: string

  /**
   * The id of the project that this asset set belongs to. (ulid)
   */
  projectId: string

  /**
   * The id of the organisation that owns this asset set. (ulid)
   *
   * Denormalised from the owning project so single-asset egress billing can
   * resolve the org without a cross-service call. Optional: asset sets created
   * before the one-time backfill do not have it populated.
   */
  orgId?: string

  /**
   * The user provided name of the asset set.
   */
  name: string

  /**
   * The type of asset set.
   */
  assetSetType: AssetSetType

  /**
   * Total count of assets in the asset set.
   */
  assetCount?: number

  /**
   * The types of assets contained in the asset set.
   * This is an array of strings representing the types of assets in the set.
   * Use values from the AssetContentType enum for consistency.
   */
  containedAssetTypes?: AssetContentType[]

  /**
   * The path to the manifest for the asset set.
   */
  manifestPath?: string

  /**
   * The storage summary for the asset set.
   */
  storageSummary: StorageSummary

  /**
   * Per-content-kind rollup over the set's assets, keyed by facet
   * (droidmap#2769). Server-derived and server-maintained; never client input.
   *
   * Facet-keyed rather than shaped by `assetSetType` because a set is a
   * container: `odm_outputs` holds an orthomosaic *and* a point cloud *and* a
   * textured model, so it carries whichever facets its contents imply. A new
   * modality is a new key — no migration, no variant. Only `raster` is built
   * today; see the design note on droidmap#2766.
   */
  assetFacets?: AssetSetFacets

  /**
   * Legacy image-shaped view of {@link assetFacets}`.raster`, derived on read.
   *
   * Kept because `estimateInputImagery` sizes photogrammetry jobs from it, and
   * it reaches that code through the asset-service client rather than through
   * this repository. **Not** stored in this shape and no longer accepted on
   * PATCH: a client-supplied rollup is why this was NULL estate-wide and every
   * cost estimate fell through to paging the whole set.
   */
  assetSetMetadata?: AssetSetMetadataImages

  /**
   * The set's capture date: the *latest* EXIF capture date across its images
   * ({@link BaseAsset.capturedAt}). Used to display and order asset sets by
   * when they were captured (not uploaded).
   *
   * Undefined until at least one contained image has an EXIF capture date.
   */
  capturedAt?: Date

  /**
   * The camera's UTC offset in minutes (east positive) for the image that
   * supplied {@link capturedAt}. Kept so a display can render the set's
   * capture date in the *photographer's* local time rather than the viewer's
   * — an early-morning flight east of UTC otherwise shows on the previous day
   * for anyone further west.
   *
   * Undefined when the offset was never determined (no EXIF offset tag, no GPS
   * clock and no resolvable position), and legitimately `0` for a camera
   * genuinely set to UTC. Derived server-side alongside {@link capturedAt};
   * never client-supplied.
   */
  capturedAtOffsetMinutes?: number

  /**
   * The set's *earliest* capture date. Pairs with {@link capturedAt} (the
   * latest) to give the capture window, and lives on the same object so a
   * caller rendering that window needs one read, not two.
   *
   * Undefined until a contained asset carries an EXIF capture date. Note the
   * window is only as trustworthy as its ends: an asset with no offset tag and
   * no GPS clock keeps its camera wall clock permanently, so a set mixing
   * anchored and unanchored images reports a window wider than the flight.
   */
  capturedAtEarliest?: Date

  /**
   * The UTC offset of the image that supplied {@link capturedAtEarliest} —
   * its own value, not a reuse of {@link capturedAtOffsetMinutes}, which
   * belongs to the *latest* image. Rendering both ends in one zone is wrong
   * by an hour or more for a set spanning a DST boundary or two zones.
   */
  capturedAtEarliestOffsetMinutes?: number

  /**
   * The upload stats for the asset set.
   */
  uploadStats?: UploadStats

  /**
   * The S3 key for the thumbnail image if one has been generated.
   * Thumbnails are only generated for supported image types.
   */
  thumbnailKey?: string

  /**
   * The time the entity was created. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  created: Date

  /**
   * The user id of the user who created this entity.
   */
  createdBy: string

  /**
   * The last time this entity was modified. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  updated: Date

  /**
   * The user id of the user who last updated this entity.
   */
  updatedBy: string
}

/**
 * Shape version for {@link AssetSetRasterFacet}.
 *
 * Per-facet rather than one version for the whole blob, so adding a
 * `pointCloud` facet later does not mark every set's `raster` data stale.
 */
export const ASSET_SET_RASTER_FACET_VERSION = 1

/**
 * Rollup over the set's raster assets — the photographs and map layers that
 * carry pixel dimensions (droidmap#2769).
 *
 * Stores what cannot be recovered from the other fields rather than the
 * narrowest possible set: `maxPixels` is the largest single image's area,
 * which is **not** `maxWidth * maxHeight` once a set is non-uniform (the
 * widest image need not be the tallest). Keeping it means
 * {@link AssetSetMetadataImages} derives exactly rather than approximately.
 *
 * Every field is maintained incrementally on upload and re-derived on delete;
 * `uniform` survives incremental maintenance because while it is true the max
 * dimensions *are* the common dimensions, and once false it stays false until
 * a recompute.
 */
export type AssetSetRasterFacet = {
  /** {@link ASSET_SET_RASTER_FACET_VERSION} the row was written at. */
  v: number
  /** Raster assets counted into this rollup. */
  fileCount: number
  /** Summed area of those assets, in megapixels. */
  totalMegapixels: number
  /** Area of the single largest asset, in pixels. */
  maxPixels: number
  /** Componentwise maxima — not necessarily one asset's dimensions. */
  maxWidth: number
  maxHeight: number
  /** Whether every counted asset shares one width and height. */
  uniform: boolean
}

/**
 * The facets a set carries. A set holds whichever its contents imply, so every
 * key is optional and absence means "nothing of that kind here".
 */
export type AssetSetFacets = {
  raster?: AssetSetRasterFacet
}

export type AssetSetMetadataImages = {
  /**
   * The total number of image files in the set.
   */
  totalFiles?: number

  /**
   * The total number of pixels in all the images in the set.
   */
  totalPixelsInMegapixels: number

  /**
   * The total size of all the images in the set in bytes.
   */
  totalSizeInBytes: number

  /**
   * The number of pixels in an individual image in this image set.
   * Expressed in megapixels.
   *
   * In the case the images are not uniform in size/shape this will be the
   * number of pixels in the largest image in the set.
   */
  imageMegapixels: number

  /**
   * Are all the images in the set the same size/shape?
   */
  uniform: boolean

  /**
   * The dimensions of the images in the set.
   *
   * In the case the images are not uniform in size/shape this will be the
   * dimension of the largest image in the set.
   */
  imageDimensions: {
    width: number
    height: number
  }
}

export type AssetSetMetadataLidar = {
  /**
   * The total number of lidar files in the set.
   */
  totalFiles: number

  /**
   * The total size of all the lidar files in the set in bytes.
   */
  totalSizeInBytes: number
}

/**
 * Inputs to create an asset set.
 */
export type CreateAssetSetInput = Omit<
  AssetSet,
  | 'id'
  | 'created'
  | 'updated'
  | 'createdBy'
  | 'updatedBy'
  | 'storageSummary'
  | 'assetFacets'
  | 'assetSetMetadata'
  // Derived from contained images' EXIF capture dates, not set at create.
  | 'capturedAt'
  | 'capturedAtOffsetMinutes'
> & {
  /**
   * Total bytes the caller is about to upload into this set, if known
   * up front (#2294). Purely a **pre-flight signal** — it is never
   * persisted; the set's real size arrives later via
   * `assetSetMetadata.totalSizeInBytes` once the upload completes.
   *
   * When present, `POST /asset-sets` checks the whole set against the
   * org's storage headroom and refuses (402) before any bytes move,
   * instead of admitting the upload and 402-ing partway through on a
   * per-file gate. Omit when the size is unknown or the set isn't being
   * populated by an upload (e.g. a job's output set) — then creation
   * behaves exactly as before.
   *
   * Client-supplied and therefore advisory: an under-declaring client
   * still meets the per-upload gate on every presigned PUT, so this
   * adds a proactive block without weakening the existing backstop.
   */
  declaredTotalBytes?: number
}

/**
 * The input required to get an AssetSet.
 */
export type GetAssetSetInput = {
  assetSetId: string
  presignedUrlType?: PresignedUrlType
}

/**
 * Inputs to update an asset set.
 *
 * NOTE: excluding projectId as this has a cascading effect on the s3
 * bucket keys/location. We can support moving assets sets between projects
 * in the future but for now we will keep it simple.
 */
export type UpdateAssetSetInput = Optional<
  Omit<
    AssetSet,
    | 'id'
    | 'projectId'
    | 'assets'
    | 'created'
    | 'updated'
    | 'createdBy'
    | 'updatedBy'
    | 'assetCount'
    | 'storageSummary'
    // Capture date and its offset are a derived rollup maintained as assets are
    // added/removed, not via the generic update path.
    | 'capturedAt'
    | 'capturedAtOffsetMinutes'
    // Same: the facet rollup and its legacy projection are maintained as assets
    // are added and removed. Accepting them here is what left them unwritten.
    | 'assetFacets'
    | 'assetSetMetadata'
  >,
  'name' | 'assetSetType' | 'uploadStats'
> & {
  assetSetId: string
}

/**
 * Correct a whole asset set's capture times by hand (droidmap#1988).
 *
 * Set-level, not per-asset, because every fault it repairs is set-shaped: a
 * camera clock that was wrong was wrong for the entire flight, and a timezone
 * inferred from position that landed on the wrong side of a boundary was wrong
 * for the entire flight too. Supply **exactly one** form.
 */
export type CorrectAssetSetCaptureTimeInput = {
  assetSetId: string
  /**
   * Shift every dated asset by this many minutes. Preserves the relative
   * timing within the flight, which is the part a wrong clock got right.
   */
  shiftMinutes?: number
  /**
   * Move the set's *earliest* capture to this instant and shift the rest with
   * it. A convenience over {@link shiftMinutes} for the common case where the
   * operator knows when the flight actually started.
   */
  firstCapturedAt?: Date
}

/** The outcome of a manual capture-time correction. */
export type CaptureTimeCorrection = {
  /** Assets whose capture time moved. */
  assetsUpdated: number
  /** The shift actually applied, whichever form the caller used. */
  shiftMinutes: number
  /** The set's capture date after the correction. */
  capturedAt?: Date
}

/**
 * The input required to delete an AssetSet.
 */
export type DeleteAssetSetInput = {
  assetSetId: string
}

/**
 * Inputs to list asset sets by project
 */
export const ASSET_SET_LIST_SORT_KEYS = [
  'name',
  'created',
  'assetCount',
  'size',
  'capturedAt',
  'capturedAtEarliest',
] as const
export type AssetSetListSortKey = (typeof ASSET_SET_LIST_SORT_KEYS)[number]

export type ListAssetSetsByProjectInput = {
  projectId: string
  /** Optional type filter for the All / Drone / ODM toolbar pills. */
  assetSetType?: AssetSetType
  /**
   * When true, each returned item carries a {@link AssetSetSummary} with the
   * set's geographic bounding box and a presigned card thumbnail URL.
   * Defaults to false so existing callers keep the cheaper query.
   */
  includeSummaries?: boolean
} & OffsetPaginationControls<AssetSetListSortKey>

export type AssetSetTypeCounts = {
  drone: number
  odm: number
}

/**
 * Axis-aligned geographic bounding box in WGS84 degrees. Derived from the
 * EXIF GPS positions of the assets in a set, so it covers camera positions
 * (not image footprints) — adequate for card previews and map framing.
 */
export type GeoBoundingBox = {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

/**
 * Card-oriented summary of an asset set: where it is and what it looks like.
 * Server-side replacement for the client fetching pages of assets just to
 * compute bounds / pick a thumbnail.
 */
/**
 * The shape `asset_sets`' rollup columns are currently written in.
 *
 * One definition, compared on read and stamped on write, so "computed" cannot
 * come to mean two different things. Bump it when the rollup gains or changes
 * a field; a row left behind is recomputed lazily rather than served stale.
 *
 * `0` is the column default and means **never computed** — distinct from
 * computed-and-empty, which is a real answer ("this set has no extent").
 */
export const ASSET_SET_SUMMARY_VERSION = 1

/** A single geographic point in WGS84 degrees. */
export type GeoPoint = {
  latitude: number
  longitude: number
}

export type AssetSetSummary = {
  /**
   * Bounding box over the GPS positions of the set's assets. Undefined when
   * no asset carries usable position metadata (e.g. ODM output sets, lidar).
   */
  boundingBox?: GeoBoundingBox

  /**
   * Centre of {@link boundingBox}, for framing a map.
   *
   * Derived on read as the midpoint of the bounds, never stored: it is
   * `(min + max) / 2` over four columns of the same row, so persisting it
   * would add a fifth number to keep in step with four that already
   * determine it. Undefined exactly when `boundingBox` is.
   */
  centroid?: GeoPoint

  /**
   * Number of assets that contributed a GPS position to the bounding box.
   * Lets callers gauge confidence (a bbox from 2 images vs 500).
   *
   * Counts *any* positioned asset — including non-rasters and uploads that
   * never completed — because that is what the live aggregate this replaced
   * counted. So it can legitimately exceed an image count.
   */
  positionedAssetCount: number

  /**
   * The set's earliest capture, and the UTC offset of the image that supplied
   * it. Pairs with {@link AssetSet.capturedAt} to give the capture window.
   *
   * Declared here as well as on {@link AssetSet} because a card renders the
   * window without fetching the set. The offset is the *earliest* image's own,
   * not the latest's — see {@link AssetSet.capturedAtEarliestOffsetMinutes}.
   */
  capturedAtEarliest?: Date
  capturedAtEarliestOffsetMinutes?: number

  /**
   * Which shape the rollup was computed in, and when.
   *
   * `0` means the values above were derived on the fly for this response
   * rather than read from the set's stored columns — either because the
   * rollup was never computed, or because it was computed in a shape older
   * than {@link ASSET_SET_SUMMARY_VERSION} and is not trusted.
   *
   * Anything else is the stored version the served values came from.
   */
  summaryVersion: number
  summaryComputedAt?: Date

  /**
   * Exact number of assets in each category (droidmap#2887) — every key of
   * {@link AssetCategoryKey} is present, zero included, so a caller can
   * render a section list without defaulting.
   *
   * Counts what the *set contains*, not what the caller has loaded, which is
   * the distinction that matters: the detail page used to derive its section
   * counts by walking the whole set (#2773).
   *
   * Only served by `GET /asset-sets/:id/summary`, never by the summaries
   * folded into a list page. The list page's summary is a read of the set's
   * own rollup columns and costs no scan of `assets` — that is the whole
   * point of #2766 — whereas this is a grouped aggregate over the set's
   * rows. It is cheap — `idx_assets_set_type` bounds it to the set, and it
   * is index-only on vacuumed pages — but it is not free, and a project list
   * page would pay it once per card for a number no card displays.
   */
  categoryCounts?: Record<AssetCategoryKey, number>

  /**
   * Presigned GET URL for a representative thumbnail. Prefers the asset
   * set's own thumbnailKey when set, otherwise the earliest asset that has
   * a generated thumbnail. Undefined when the set has no thumbnails.
   */
  thumbnailPresignedUrl?: string
  thumbnailPresignedUrlExpiresAt?: Date
}

/**
 * An asset set decorated with its card summary. `summary` is only populated
 * when the caller asked for it (e.g. `includeSummaries` on list).
 */
export type AssetSetWithSummary = AssetSet & {
  summary?: AssetSetSummary
}

/**
 * The input required to get an asset set's summary.
 */
export type GetAssetSetSummaryInput = {
  assetSetId: string
}

/**
 * Project-level rollup of asset geography and imagery, served by the
 * asset-service (which owns the asset data). Backs the project cards: one
 * call yields the map extent, a hero image and the set count.
 */
export type ProjectAssetSummary = {
  projectId: string

  /**
   * Bounding box over the GPS positions of every asset in the project.
   * Undefined when no asset carries usable position metadata.
   */
  boundingBox?: GeoBoundingBox

  /** Number of assets that contributed a GPS position to the bounding box. */
  positionedAssetCount: number

  /** Total asset sets in the project. */
  assetSetCount: number

  /**
   * Presigned GET URL for a hero thumbnail. Picks from drone-image sets
   * first (derived outputs make poor banners), newest set first.
   */
  thumbnailPresignedUrl?: string
  thumbnailPresignedUrlExpiresAt?: Date
}

/**
 * The input required to get a project's asset summary.
 */
export type GetProjectAssetSummaryInput = {
  projectId: string
}

/**
 * Response shape for {@link ListAssetSetsByProjectInput}. Extends
 * {@link OffsetPaged} with per-type counts so the toolbar pills reflect the
 * full filtered dataset, not just the current page. Items carry a `summary`
 * only when the request set `includeSummaries`.
 */
export type ListAssetSetsByProjectResult = OffsetPaged<AssetSetWithSummary> & {
  typeCounts: AssetSetTypeCounts
}

/**
 * ManifestEntry is a type that represents an entry in the manifest for an
 * asset set.
 */
export interface ManifestEntry {
  fileName: string
  folderPath: string
  fileExtension: string
  filePath: string
  fileSize: number
  assetType: AssetType
  assetId: string
}

/**
 * Manifest is a type that represents the manifest for an asset set.
 */
export interface Manifest {
  generatedAt: string
  totalFiles: number
  totalSize: number
  files: ManifestEntry[]
}

/* ---------------------------------- Asset --------------------------------- */

/**
 * Largest accepted pixel count for one side of an image (droidmap#2843).
 *
 * Far past any real sensor — the biggest medium-format backs are ~20k px, and
 * a large stitched orthomosaic runs to low hundreds of thousands — and chosen
 * so the products it feeds stay exact rather than merely finite: `width *
 * height` peaks at 1e12, and summing that across even a 5,000-image set stays
 * inside 2^53, so an asset set's pixel total is an exact integer.
 *
 * Lives in the contract because both sides of the boundary need it: the
 * asset-service refuses anything outside it at the API edge and when
 * projecting onto `assets.width`/`height`, and `validateImage` in
 * shared-service-utils refuses it again before multiplying, since rows stored
 * before this bound existed can hold anything at all.
 */
export const MAX_IMAGE_DIMENSION = 1_000_000

export type ImageMetadataDimensions = {
  width: number
  height: number
}

export type ImageMetadataPosition = {
  latitude: number
  longitude: number
  absoluteAltitude?: number
  relativeAltitude?: number
}

/**
 * Camera metadata extracted from EXIF
 */
export type ImageMetadataCamera = {
  make?: string
  model?: string
  focalLength?: number
  focalLengthIn35mm?: number
  calibratedFocalLength?: number
  exifImageWidth?: number
  exifImageHeight?: number
}

/**
 * Drone metadata extracted from EXIF
 */
export type ImageMetadataDrone = {
  gimbalRollDegree?: number
  gimbalYawDegree?: number
  gimbalPitchDegree?: number
  flightRollDegree?: number
  flightYawDegree?: number
  flightPitchDegree?: number
  flightXSpeed?: number
  flightYSpeed?: number
  flightZSpeed?: number
}

/**
 * Complete metadata extracted from image EXIF
 */
export type AssetMetadataImage = {
  dimensions?: ImageMetadataDimensions
  position?: ImageMetadataPosition
  /**
   * Capture time as an ISO-8601 UTC instant.
   *
   * A *true* instant unless {@link AssetMetadataImage.timestampSource} is
   * `'local-assumed'` — and an instant only when that field is present at all.
   * Values written before droidmap#1988 carry no source and follow the legacy
   * convention: the camera's local wall clock stamped with a `Z` it never
   * earned. See {@link CaptureTimestampSource}.
   */
  timestamp?: string
  /** How {@link AssetMetadataImage.timestamp} was derived. */
  timestampSource?: CaptureTimestampSource
  /**
   * The camera's UTC offset in minutes, east positive. Kept so a display can
   * show the photographer's own wall clock — the flight time they'd recognise
   * — without re-deriving it. Absent for `'local-assumed'`; legitimately `0`
   * for a camera genuinely set to UTC.
   */
  timestampOffsetMinutes?: number
  camera?: ImageMetadataCamera
  drone?: ImageMetadataDrone
}

export type AssetMetadataLidar = {
  timestamp?: string
}

export enum AssetStorageState {
  None = 'none',
  standard = 'standard',
  standardIA = 'standardIA',
  oneZoneIA = 'oneZoneIA',
  glacierIR = 'glacierIR',
  glacierFlex = 'glacierFlex',
  glacierDA = 'glacierDA',
}

export type BaseAsset<TMetadata = AssetMetadataImage | AssetMetadataLidar> = {
  /**
   * Discriminator for the kind of asset.
   * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#discriminated-unions
   *
   * This is used to determine if the asset is a presigned url asset or not.
   */
  kind: 'asset'

  /**
   * The unique id of the asset. (ulid)
   */
  id: string

  /**
   * The id of the asset set that this asset belongs to. (ulid)
   */
  assetSetId: string

  /**
   * The id of the project that owns this asset (ulid). Denormalized from the
   * parent asset set at creation and immutable thereafter, so tenancy
   * authorization can resolve the asset's true project from a single read.
   *
   * Server-populated and always present on rows read from the store (the
   * `project_id` column is NOT NULL). Optional on the type only so the many
   * hand-built `Asset` fixtures across the codebase don't have to supply it;
   * the service derives it and never accepts it as client input.
   */
  projectId?: string

  /**
   * The filename of the asset.
   */
  name: string

  /**
   * The file type of the asset.
   */
  assetType: AssetType

  /**
   * The folder path this file live in relative to the base of the asset set.
   */
  folderPath?: string

  /**
   * The S3 bucket that this asset is stored in.
   */
  bucket: string

  /**
   * The S3 key prefix that this asset is stored under.
   */
  prefix: string

  /**
   * The S3 key that this asset is stored under.
   *
   * This is a concatenation of the prefix, folderPath and name.
   * We precompute this value so that we can use it as a sort key
   * and do lookups by bucket and key.
   */
  key: string

  /**
   * Has the asset upload to S3 been confirmed.
   */
  uploadConfirmed: boolean

  /**
   * Size of the file in bytes
   */
  size?: number

  /**
   * Flexible metadata storage for asset-specific data.
   * For drone images, this can include GPS coordinates.
   * For other asset types, this can store relevant metadata.
   */
  assetMetadata?: TMetadata

  /**
   * This field contains the value of the ETag field returned in the
   * object created event. For files uploaded in one part this is the md5
   * checksum of the file. For files uploaded in multiple parts this is
   * the md5 checksum of the concatenated md5 checksums of each part.
   *
   * https://docs.aws.amazon.com/AmazonS3/latest/userguide/checking-object-integrity.html
   * https://stackoverflow.com/questions/1775816/how-to-get-the-md5sum-of-a-file-on-amazons-s3
   */
  checksum?: string

  /**
   * What storage tier the asset is currently stored in.
   */
  storageState: AssetStorageState

  /**
   * The S3 key for the thumbnail image if one has been generated.
   * Thumbnails are only generated for supported image types.
   */
  thumbnailKey?: string

  /**
   * The capture date of the asset, taken from the EXIF metadata of the image
   * (e.g. `DateTimeOriginal`). This is when the photo was *taken*, as opposed
   * to {@link created}/{@link updated} which track when the record was
   * uploaded/modified. Promoted from `assetMetadata.timestamp` to a
   * first-class field so assets can be ordered by capture date.
   *
   * Undefined for assets without EXIF capture data (e.g. most non-image types,
   * or images stripped of EXIF).
   */
  capturedAt?: Date

  /**
   * The time the entity was created. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  created: Date

  /**
   * The user id of the user who created this entity.
   */
  createdBy: string

  /**
   * The last time this entity was modified. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  updated: Date

  /**
   * The user id of the user who last updated this entity.
   */
  updatedBy: string
}

export type ImageAsset = BaseAsset<AssetMetadataImage>
export type LidarAsset = BaseAsset<AssetMetadataLidar>

export type Asset = ImageAsset | LidarAsset

/**
 * A holder for presigned url information
 */
export type WithPresignedUrl = {
  presignedUrl: string
  presignedUrlExpiresAt: Date
}

/**
 * Multipart upload presigned URLs for uploading large files
 */
export type WithMultipartPresignedUrls = {
  uploadId: string
  key: string
  parts: Array<{
    partNumber: number
    presignedUrl: string
  }>
  presignedUrlExpiresAt: Date
}

export enum PresignedUrlType {
  GET = 'get',
  PUT = 'put',
  MULTIPART = 'multipart',
}

export const presignedUrlTypeFromString = (
  presignedUrlTypeString: string,
): PresignedUrlType | undefined => {
  switch (presignedUrlTypeString) {
    case 'get':
      return PresignedUrlType.GET
    case 'put':
      return PresignedUrlType.PUT
    case 'multipart':
      return PresignedUrlType.MULTIPART
    default:
      throw new Error(`Unknown presigned url type: ${presignedUrlTypeString}`)
  }
}

/**
 * For the client we want to return the asset with a presigned url
 * that allows either a get or put operation. We don't want to return
 * the bucket and prefix as that is an implementation detail.
 */
export type BaseAssetWithPresignedUrl<
  TMetadata = AssetMetadataImage | AssetMetadataLidar,
> = Omit<
  BaseAsset<TMetadata>,
  'bucket' | 'prefix' | 'key' | 'storageState' | 'kind' | 'thumbnailKey'
> &
  WithPresignedUrl & {
    /**
     * Discriminator for the kind of asset.
     * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#discriminated-unions
     */
    kind: 'assetWithPresignedUrl'
    /**
     * Presigned URL for the thumbnail image if one exists.
     * Only populated for image assets that have thumbnails generated.
     */
    thumbnailPresignedUrl?: string
    thumbnailPresignedUrlExpiresAt?: Date
  }

export type ImageAssetWithPresignedUrl =
  BaseAssetWithPresignedUrl<AssetMetadataImage>
export type LidarAssetWithPresignedUrl =
  BaseAssetWithPresignedUrl<AssetMetadataLidar>

export type AssetWithPresignedUrl =
  ImageAssetWithPresignedUrl | LidarAssetWithPresignedUrl

/**
 * Inputs to create an asset.
 */
export type CreateAssetInput = Omit<
  Asset,
  | 'kind'
  | 'id'
  // Derived server-side from the parent asset set, never client-supplied.
  | 'projectId'
  | 'bucket'
  | 'prefix'
  | 'key'
  | 'uploadConfirmed'
  | 'storageState'
  | 'size'
  | 'checksum'
  // Derived server-side from EXIF on upload-confirm, not supplied at create.
  | 'capturedAt'
  | 'created'
  | 'updated'
  | 'createdBy'
  | 'updatedBy'
> & {
  presignedUrlType?: PresignedUrlType
}

/**
 * The input required to get an Asset.
 */
export type GetAssetInput = {
  assetId: string
  presignedUrlType?: PresignedUrlType
}

/**
 * The input required to get multiple assets.
 */
export type GetAssetsInput = {
  assetIds: string[]
  presignedUrlType?: PresignedUrlType
}

/**
 * The input required to get an Asset by path.
 */
export type GetAssetByPathInput = {
  assetSetId: string
  path: string
  presignedUrlType?: PresignedUrlType
}

/**
 * The input required when and Asset is uploaded to S3.
 */
export type UploadedAssetInput = {
  bucket: string
  key: string
  size: number
  checksum: string
}

/**
 * Inputs to update an asset.
 */
export type UpdateAssetInput = Optional<
  Omit<
    Asset,
    | 'kind'
    | 'id'
    // Immutable and server-owned; the parent set never changes an asset's org.
    | 'projectId'
    | 'bucket'
    | 'prefix'
    | 'key'
    | 'created'
    | 'updated'
    | 'createdBy'
    | 'updatedBy'
  >,
  | 'name'
  | 'assetSetId'
  | 'assetType'
  | 'folderPath'
  | 'uploadConfirmed'
  | 'size'
  | 'assetMetadata'
  | 'capturedAt'
  | 'thumbnailKey'
  | 'checksum'
  | 'storageState'
> & {
  assetId: string
}

/**
 * The subset of fields that can be updated by a client
 */
export type UpdateAssetExternalInput = Optional<
  Omit<
    Asset,
    | 'kind'
    | 'id'
    | 'projectId'
    | 'bucket'
    | 'prefix'
    | 'key'
    | 'size'
    | 'assetSetId'
    | 'uploadConfirmed'
    | 'storageState'
    | 'checksum'
    // Capture date is derived from EXIF server-side; not client-editable.
    | 'capturedAt'
    | 'created'
    | 'updated'
    | 'createdBy'
    | 'updatedBy'
  >,
  'name' | 'assetType' | 'folderPath' | 'assetMetadata'
> & {
  assetId: string
}

/**
 * The input required to delete an Asset.
 */
export type DeleteAssetInput = {
  assetId: string
}

/**
 * Per-asset fields a map or a file list needs, without a presigned URL
 * (droidmap#2771). Roughly 80 bytes an asset against ~1.5-2.5 KB for the
 * signed shape.
 *
 * No `thumbnailKey`: handing a client an S3 key is what lets one asset's
 * thumbnail be pointed at another's bytes, which is why `addPresignedUrl`
 * strips it. `hasThumbnail` plus the asset id is enough to build the
 * `/assets/:id/thumbnail` redirect path (#2772).
 */
export type SlimAsset = {
  id: string
  name: string
  assetType: AssetType
  size?: number
  capturedAt?: Date
  /** Promoted columns (#2818); both present or neither. */
  lat?: number
  lng?: number
  /** Still read from EXIF metadata — relative preferred over absolute. */
  altitude?: number
  width?: number
  height?: number
  /**
   * What a ground footprint needs (droidmap#3019), so an uploaded set can be
   * assessed for overlap from the projection alone. `relativeAltitude` is
   * carried apart from `altitude` because only height above the launch point
   * sizes a footprint — `altitude` may have fallen back to sea level.
   */
  relativeAltitude?: number
  focalLength?: number
  focalLengthIn35mm?: number
  calibratedFocalLength?: number
  gimbalPitchDegree?: number
  gimbalYawDegree?: number
  hasThumbnail: boolean
}

/** Which shape a listing returns. Absent means the full asset. */
/**
 * Largest page the slim projection accepts.
 *
 * Deliberately far above {@link MAX_PAGE_SIZE}: the point of the projection is
 * that a typical set is one or two round trips, and at ~80 bytes an asset a
 * full page is well under a megabyte. The signed listing keeps its own
 * (unbounded) behaviour — capping it would change what existing callers get.
 */
export const SLIM_MAX_PAGE_SIZE = 1000

export const ASSET_PROJECTIONS = ['slim'] as const
export type AssetProjection = (typeof ASSET_PROJECTIONS)[number]

export type ListAssetsByAssetSetInput = {
  assetSetId: string
  presignedUrlType?: PresignedUrlType
  projection?: AssetProjection
  /**
   * Restrict the listing to one UI category (droidmap#2887).
   *
   * Expanded server-side using the *set's* type, because the same asset type
   * falls into different categories depending on what holds it — a JPEG is
   * `imagery` in a drone-image set and `model` in an ODM output set. A client
   * cannot expand it correctly without duplicating that rule.
   */
  category?: AssetCategoryKey
  /**
   * Restrict the listing to specific asset types. Takes precedence over
   * {@link category}, for callers that want a set no category names — the
   * advanced map wants georeferenced rasters, which is `map` minus plain
   * `.tif`, a distinction that exists because those carry no CRS.
   */
  assetTypes?: readonly AssetType[]
} & PaginationControls

/**
 * AssetWithPresignedUrl type guard
 */
export const isAssetWithPresignedUrl = (
  asset: Asset | AssetWithPresignedUrl,
): asset is AssetWithPresignedUrl => {
  return (asset as AssetWithPresignedUrl).presignedUrl !== undefined
}

/* ------------------------------ DownloadJob ------------------------------- */

/**
 * Status of an asset set download job
 */
export enum DownloadJobStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

/**
 * A download job represents an async request to download all assets
 * in an asset set as a ZIP archive.
 */
export type DownloadJob = {
  /**
   * The unique id of the download job. (ulid)
   */
  id: string

  /**
   * The id of the asset set being downloaded. (ulid)
   */
  assetSetId: string

  /**
   * The id of the project that the asset set belongs to. (ulid)
   */
  projectId: string

  /**
   * The id of the organization. (ulid)
   */
  orgId: string

  /**
   * The name of the asset set (for display and ZIP filename)
   */
  assetSetName: string

  /**
   * The current status of the download job.
   */
  status: DownloadJobStatus

  /**
   * The total number of assets to be downloaded.
   */
  totalAssets?: number

  /**
   * The number of assets that have been processed.
   */
  processedAssets: number

  /**
   * The total size of all assets in bytes.
   */
  totalBytes?: number

  /**
   * The number of bytes that have been processed.
   */
  processedBytes: number

  /**
   * The S3 key of the generated ZIP file.
   */
  downloadKey?: string

  /**
   * The presigned URL for downloading the ZIP file.
   * Only populated when status is 'completed'.
   */
  downloadUrl?: string

  /**
   * When the download URL expires.
   */
  downloadUrlExpiresAt?: Date

  /**
   * Error message if the job failed.
   */
  errorMessage?: string

  /**
   * Number of times this job has been retried.
   */
  retryCount: number

  /**
   * The number of assets that failed to be added to the archive.
   * Defaults to 0 when the job is created.
   */
  failedAssets?: number

  /**
   * The IDs of assets that failed to be added to the archive.
   * Limited to first 100 to avoid exceeding DynamoDB item size limits.
   */
  failedAssetIds?: string[]

  /**
   * The IDs of assets that succeeded in being added to the archive. Captured
   * once on job completion and used by the daily-egress-flusher to map each
   * `download_job_egress_daily` row back to the per-asset events it must emit.
   * Unbounded (each id becomes a billable event — capping would silently
   * under-bill large jobs).
   */
  succeededAssetIds?: string[]

  /**
   * TTL timestamp for auto-cleanup (Unix timestamp in seconds).
   */
  ttl: number

  /**
   * The share link this job was created for, when the job was initiated by a
   * public share download rather than an authenticated user. Drives the
   * `share-downloads/` S3 prefix (long-lived staging) and per-link job reuse.
   */
  shareLinkId?: string

  /**
   * SHA-256 hex fingerprint of the asset set contents captured at job
   * creation (assetCount + the six storage-class byte counters). Compared at
   * download-click time to lazily void a staged share ZIP whose source asset
   * set has since changed.
   */
  contentFingerprint?: string

  /**
   * When this staged share ZIP was invalidated because the asset set's
   * contents changed. Voided jobs are never reused; the row survives so the
   * egress flusher can still attribute bytes served during the presign grace
   * window.
   */
  voidedAt?: Date

  /**
   * The time the job was created.
   */
  created: Date

  /**
   * The user id of the user who created this job.
   */
  createdBy: string

  /**
   * The last time this job was modified.
   */
  updated: Date

  /**
   * The user id of the user who last updated this job.
   */
  updatedBy: string
}

/**
 * Input to initiate a download job for an asset set.
 */
export type InitiateAssetSetDownloadInput = {
  assetSetId: string
}

/**
 * Input to get a download job.
 */
export type GetDownloadJobInput = {
  downloadJobId: string
}

/**
 * Response from initiating a download job.
 */
export type InitiateAssetSetDownloadResponse = Pick<
  DownloadJob,
  | 'id'
  | 'assetSetId'
  | 'assetSetName'
  | 'status'
  | 'totalAssets'
  | 'totalBytes'
  | 'created'
>

/**
 * Response from getting a download job status.
 */
export type GetDownloadJobResponse = Pick<
  DownloadJob,
  | 'id'
  | 'assetSetId'
  | 'assetSetName'
  | 'status'
  | 'totalAssets'
  | 'processedAssets'
  | 'totalBytes'
  | 'processedBytes'
  | 'failedAssets'
  | 'failedAssetIds'
  | 'downloadUrl'
  | 'downloadUrlExpiresAt'
  | 'errorMessage'
  | 'created'
  | 'updated'
>
